/**
 * 인증 컨텍스트
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { LoginRequest, AuthState } from '../types';
import { ROUTES } from '../../../shared/constants/routes';

interface AuthContextType {
  authState: AuthState;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 컴포넌트 마운트 시 저장된 토큰 확인
    const { accessToken, refreshToken, user } = authService.getStoredTokens();
    if (accessToken && user) {
      setAuthState({
        isAuthenticated: true,
        user,
        accessToken,
        refreshToken,
      });
    }
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login(credentials);
      authService.saveTokens(response.accessToken, response.refreshToken, response.user);

      setAuthState({
        isAuthenticated: true,
        user: response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });

      navigate(ROUTES.ADMIN.DASHBOARD);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await authService.logout();
    setAuthState({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
    });
    navigate(ROUTES.ADMIN.LOGIN);
  };

  return (
    <AuthContext.Provider value={{ authState, login, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
